// Deterministic replays for Lesson 3.12 ("Pick the Model, Not Only the Prompt"). Three graphics
// replay runs recorded on 9 October 2026 through the workspace gateway: the thirty-note golden set
// under suggester-v3 (temperature 0, max_tokens 60) on four models, two routers over it, and eight
// one-answer questions at three reasoning efforts (max_tokens 1,200). The cache graphic computes a
// bill from the recorded 2,637-token prefix and a discount the price list does not show. Prices are
// Bedrock's us-east-1 list prices that day, per million tokens. Nothing here calls a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export const thousands = (value: number) => value.toLocaleString('en-US');
export const dollars = (value: number) => `$${value.toFixed(4)}`;
export const msText = (ms: number) => `${thousands(ms)} ms`;
export const NOTES = 30;
export const ofThirty = (count: number) => `${count} of ${NOTES}`;

// --- choose-models: the thirty notes on four models ------------------------------------------

export const CHOOSE_MODELS = ['flash', 'glm5', 'deepseek', 'gptoss'] as const;
export type ChooseModel = (typeof CHOOSE_MODELS)[number];
export const chooseModelLabels: Record<ChooseModel, string> = {
  flash: 'GLM 4.7 Flash',
  glm5: 'GLM 5',
  deepseek: 'DeepSeek v3.2',
  gptoss: 'gpt-oss-120b',
};
export const toChooseModel = (value: string): ChooseModel =>
  CHOOSE_MODELS.find((id) => id === value) ?? 'flash';
export interface GoldenRun {
  passed: number;
  /** The note numbers that failed, as text. */
  missed: string;
  promptTokens: number;
  completionTokens: number;
  p50: number;
  p95: number;
  /** The whole run of thirty notes at list price, in dollars. */
  cost: number;
  /** List price per million tokens, input then output. */
  price: string;
  message: string;
  tone: Tone;
}
export const goldenRuns: Record<ChooseModel, GoldenRun> = {
  flash: {
    passed: 19,
    missed: '5, 6, 8, 9, 16, 19, 20, 22, 24, 26, 28',
    promptTokens: 8094,
    completionTokens: 523,
    p50: 1018,
    p95: 1856,
    cost: 0.0008,
    price: '$0.07 in, $0.40 out',
    tone: 'warning',
    message: 'The cheapest model costs a thirteenth and misses eleven notes.',
  },
  glm5: {
    passed: 28,
    missed: '19, 26',
    promptTokens: 8094,
    completionTokens: 707,
    p50: 1020,
    p95: 6468,
    cost: 0.0104,
    price: '$1.00 in, $3.20 out',
    tone: 'success',
    message: 'The strong model passes 28 and costs the most of the four.',
  },
  deepseek: {
    passed: 24,
    missed: '9, 16, 19, 22, 25, 26',
    promptTokens: 8077,
    completionTokens: 826,
    p50: 1193,
    p95: 1989,
    cost: 0.0065,
    price: '$0.62 in, $1.85 out',
    tone: 'neutral',
    message: "DeepSeek passes 24 at about two thirds of the strong model's cost.",
  },
  gptoss: {
    passed: 0,
    missed: 'all thirty, cut by the budget or not JSON',
    promptTokens: 10003,
    completionTokens: 5837,
    p50: 1758,
    p95: 3586,
    cost: 0.005,
    price: '$0.15 in, $0.60 out',
    tone: 'warning',
    message: 'The largest model scored zero for a parsing reason, not a knowledge one.',
  },
};
export const runText = (run: GoldenRun) =>
  [
    `${thousands(run.promptTokens)} prompt and ${thousands(run.completionTokens)} completion tokens`,
    `p95 ${msText(run.p95)}`,
    `list price ${run.price} per million`,
    `missed: ${run.missed}`,
  ].join('\n');
export interface ModelsState {
  model: ChooseModel;
  run: boolean;
}
export const initialModels = (): ModelsState => ({ model: 'flash', run: false });
export type ModelsAction =
  { type: 'model'; value: ChooseModel } | { type: 'run' } | { type: 'reset' };
export function modelsTransition(state: ModelsState, action: ModelsAction): ModelsState {
  switch (action.type) {
    case 'reset':
      return initialModels();
    case 'model':
      return { model: action.value, run: false };
    case 'run':
      return { ...state, run: true };
  }
}
export const MODELS_START_MESSAGE = 'Choose a model, then press Run the thirty notes.';
export const MODELS_REFINEMENT = 'Refinement: score and cost are two columns, never one.';
export function modelsSummary(state: ModelsState): Summary {
  if (!state.run) return { tone: 'neutral', message: MODELS_START_MESSAGE };
  const run = goldenRuns[state.model];
  return { tone: run.tone, message: run.message };
}

// --- choose-route: small model first, escalate on a signal -----------------------------------

export const SIGNALS = ['none', 'strong-judge', 'small-judge'] as const;
export type Signal = (typeof SIGNALS)[number];
export const signalLabels: Record<Signal, string> = {
  none: 'none, small model only',
  'strong-judge': 'the strong model as judge',
  'small-judge': 'the small model as judge',
};
export const toSignal = (value: string): Signal => SIGNALS.find((id) => id === value) ?? 'none';
export const STRONG_ALONE_SCORE = 28;
export const STRONG_ALONE_COST = 0.0104;
export interface RouteRun {
  escalated: readonly number[];
  score: number;
  cost: number;
  /** The cost against GLM 5 alone, in whole percent. */
  percentOfStrong: number;
  breakdown: string;
  agreement: string;
  message: string;
  tone: Tone;
}
export const routeRuns: Record<Signal, RouteRun> = {
  none: {
    escalated: [],
    score: 19,
    cost: 0.0008,
    percentOfStrong: 8,
    breakdown: 'small model $0.0008, no judge, no strong model',
    agreement: 'validation only: all thirty small-model replies fit the shape',
    tone: 'warning',
    message: 'Validation escalated nothing, so the router scores what the small model scores.',
  },
  'strong-judge': {
    escalated: [5, 6, 8, 9, 15, 16, 19, 20, 22, 23, 24, 26, 27, 28, 29],
    score: 28,
    cost: 0.015,
    percentOfStrong: 145,
    breakdown: 'small $0.0008 + judge $0.0088 + strong on the escalated $0.0055',
    agreement: 'the judge agreed with the code grader on 26 of 30',
    tone: 'warning',
    message: 'The strong judge reached 28 and cost more than the strong model alone.',
  },
  'small-judge': {
    escalated: [6, 8, 9, 15, 20, 22, 23],
    score: 24,
    cost: 0.0039,
    percentOfStrong: 37,
    breakdown: 'small $0.0008 + judge $0.0007 + strong on the escalated $0.0024',
    agreement: 'the judge agreed with the code grader on 22 of 30',
    tone: 'neutral',
    message: "The small judge reached 24 at about a third of the strong model's cost.",
  },
};
export const escalatedText = (run: RouteRun) => ofThirty(run.escalated.length);
export const againstStrongText = (run: RouteRun) =>
  `${dollars(run.cost)}, ${run.percentOfStrong} percent`;
export const routeText = (run: RouteRun) =>
  [
    `escalated: ${run.escalated.length ? run.escalated.join(', ') : 'none'}`,
    run.breakdown,
    run.agreement,
    `GLM 5 alone: ${ofThirty(STRONG_ALONE_SCORE)} for ${dollars(STRONG_ALONE_COST)}`,
  ].join('\n');
export interface RouteState {
  signal: Signal;
  routed: boolean;
}
export const initialRoute = (): RouteState => ({ signal: 'none', routed: false });
export type RouteAction = { type: 'signal'; value: Signal } | { type: 'route' } | { type: 'reset' };
export function routeTransition(state: RouteState, action: RouteAction): RouteState {
  switch (action.type) {
    case 'reset':
      return initialRoute();
    case 'signal':
      return { signal: action.value, routed: false };
    case 'route':
      return { ...state, routed: true };
  }
}
export const ROUTE_START_MESSAGE =
  'Choose an escalation signal, then press Route the thirty notes.';
export const ROUTE_REFINEMENT = 'Refinement: a cheap and accurate signal, or no router.';
export function routeSummary(state: RouteState): Summary {
  if (!state.routed) return { tone: 'neutral', message: ROUTE_START_MESSAGE };
  const run = routeRuns[state.signal];
  return { tone: run.tone, message: run.message };
}

// --- choose-thinking: eight questions at three efforts ---------------------------------------

export const THINK_MODELS = ['flash', 'glm5'] as const;
export type ThinkModel = (typeof THINK_MODELS)[number];
export const thinkModelLabels: Record<ThinkModel, string> = {
  flash: 'GLM 4.7 Flash',
  glm5: 'GLM 5',
};
export const toThinkModel = (value: string): ThinkModel =>
  THINK_MODELS.find((id) => id === value) ?? 'flash';
export const EFFORTS = ['none', 'low', 'high'] as const;
export type Effort = (typeof EFFORTS)[number];
export const toEffort = (value: string): Effort => EFFORTS.find((id) => id === value) ?? 'none';
export const QUESTIONS = 8;
export const THINK_BUDGET = 1200;
export interface ThinkRun {
  right: number;
  /** Completion tokens across the eight questions, reasoning included. */
  tokens: number;
  /** Mean latency in seconds. */
  seconds: number;
  /** Answers the 1,200-token budget cut before the answer. */
  cut: number;
  message: string;
  tone: Tone;
}
const think = (
  right: number,
  tokens: number,
  seconds: number,
  cut: number,
  tone: Tone,
  message: string,
): ThinkRun => ({ right, tokens, seconds, cut, tone, message });
export const thinkRuns: Record<ThinkModel, Record<Effort, ThinkRun>> = {
  flash: {
    none: think(
      3,
      18,
      1.6,
      0,
      'neutral',
      'Without reasoning the small model got three of eight right.',
    ),
    low: think(5, 20, 1.4, 0, 'success', 'Low effort added two right answers for two more tokens.'),
    high: think(4, 4976, 15.7, 2, 'warning', 'High effort cut two answers and scored below low.'),
  },
  glm5: {
    none: think(
      6,
      17,
      0.7,
      0,
      'neutral',
      'Without reasoning the strong model got six of eight right.',
    ),
    low: think(6, 17, 0.6, 0, 'neutral', 'Low effort changed nothing on the strong model.'),
    high: think(
      7,
      4457,
      8.1,
      1,
      'neutral',
      'High effort bought one more right answer for 260 times the tokens.',
    ),
  },
};
export const ofEight = (count: number) => `${count} of ${QUESTIONS}`;
export const secondsText = (seconds: number) => `${seconds.toFixed(1)} s`;
export const thinkText = (run: ThinkRun) =>
  [
    `mean latency ${secondsText(run.seconds)}`,
    `budget ${thousands(THINK_BUDGET)} tokens for the working-out and the answer`,
    'the working-out arrives as a <reasoning> block in the content',
  ].join('\n');
export interface ThinkState {
  model: ThinkModel;
  effort: Effort;
  asked: boolean;
}
export const initialThink = (): ThinkState => ({ model: 'flash', effort: 'none', asked: false });
export type ThinkAction =
  | { type: 'model'; value: ThinkModel }
  | { type: 'effort'; value: Effort }
  | { type: 'ask' }
  | { type: 'reset' };
export function thinkTransition(state: ThinkState, action: ThinkAction): ThinkState {
  switch (action.type) {
    case 'reset':
      return initialThink();
    case 'model':
      return { ...state, model: action.value, asked: false };
    case 'effort':
      return { ...state, effort: action.value, asked: false };
    case 'ask':
      return { ...state, asked: true };
  }
}
export const thinkRun = (state: ThinkState) => thinkRuns[state.model][state.effort];
export const THINK_START_MESSAGE =
  'Choose a model and an effort, then press Ask the eight questions.';
export const THINK_REFINEMENT = 'Refinement: a thinking budget is a budget.';
export function thinkSummary(state: ThinkState): Summary {
  if (!state.asked) return { tone: 'neutral', message: THINK_START_MESSAGE };
  const run = thinkRun(state);
  return { tone: run.tone, message: run.message };
}

// --- choose-cache: what a discount on the cached prefix would save ----------------------------

export const DISCOUNTS = ['none', 'half', 'nine-tenths'] as const;
export type Discount = (typeof DISCOUNTS)[number];
export const discountLabels: Record<Discount, string> = {
  none: "none visible, as this gateway's price list shows",
  half: 'half',
  'nine-tenths': 'nine tenths',
};
/** The share of each cached prefix token that is still billed. */
export const billedShare: Record<Discount, number> = { none: 1, half: 0.5, 'nine-tenths': 0.1 };
export const toDiscount = (value: string): Discount =>
  DISCOUNTS.find((id) => id === value) ?? 'none';
export const CALLS = [30, 300, 3000] as const;
export type Calls = (typeof CALLS)[number];
export const toCalls = (value: string): Calls => CALLS.find((c) => String(c) === value) ?? 30;
export const PREFIX_TOKENS = 2637;
/** GLM 5's list price in dollars per million input tokens. */
export const INPUT_PRICE = 1;
export const fullTokens = (calls: number) => calls * PREFIX_TOKENS;
/** The first call pays the whole prefix; every later call pays the billed share of it. */
export const billedTokens = (discount: Discount, calls: number) =>
  PREFIX_TOKENS + Math.round((calls - 1) * PREFIX_TOKENS * billedShare[discount]);
export const inputCost = (tokens: number) => (tokens / 1_000_000) * INPUT_PRICE;
export const savedDollars = (discount: Discount, calls: number) =>
  inputCost(fullTokens(calls)) - inputCost(billedTokens(discount, calls));
export const percentOff = (discount: Discount) => Math.round((1 - billedShare[discount]) * 100);
export const cacheText = (discount: Discount, calls: number) =>
  [
    `first call: ${thousands(PREFIX_TOKENS)} tokens at full price`,
    `${thousands(calls - 1)} later calls: ${thousands(PREFIX_TOKENS)} tokens each${
      discount === 'none' ? ' at full price' : `, ${percentOff(discount)}% off the cached prefix`
    }`,
    `= ${thousands(billedTokens(discount, calls))} input tokens billed`,
  ].join('\n');
export interface CacheState {
  discount: Discount;
  calls: Calls;
  computed: boolean;
}
export const initialCache = (): CacheState => ({ discount: 'none', calls: 30, computed: false });
export type CacheAction =
  | { type: 'discount'; value: Discount }
  | { type: 'calls'; value: Calls }
  | { type: 'compute' }
  | { type: 'reset' };
export function cacheTransition(state: CacheState, action: CacheAction): CacheState {
  switch (action.type) {
    case 'reset':
      return initialCache();
    case 'discount':
      return { ...state, discount: action.value, computed: false };
    case 'calls':
      return { ...state, calls: action.value, computed: false };
    case 'compute':
      return { ...state, computed: true };
  }
}
export const CACHE_START_MESSAGE = 'Choose a discount and a number of calls, then press Compute.';
export const NO_DISCOUNT_MESSAGE =
  'With no cache rate on the price list, every call bills the whole prefix.';
export const HALF_MESSAGE = 'Half off the cached prefix nearly halves the input bill.';
export const NINE_TENTHS_MESSAGE =
  'Nine tenths off the cached prefix removes most of the input bill.';
export const CACHE_REFINEMENT =
  'Refinement: usage showed cached tokens on three of five flash calls, and the price list shows no cache rate.';
export function cacheSummary(state: CacheState): Summary {
  if (!state.computed) return { tone: 'neutral', message: CACHE_START_MESSAGE };
  switch (state.discount) {
    case 'none':
      return { tone: 'warning', message: NO_DISCOUNT_MESSAGE };
    case 'half':
      return { tone: 'neutral', message: HALF_MESSAGE };
    case 'nine-tenths':
      return { tone: 'success', message: NINE_TENTHS_MESSAGE };
  }
}
